/* global gdAlertify */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatDate, formatMoney, formatSectorLabel } from "../utils/utils";

const CAMPOS = [
    { key: "valorTurnoPaciente", label: "Valor turno" },
    { key: "valorTurnoBasePaciente", label: "Valor base" },
    { key: "valorColacionPaciente", label: "Colacion" },
    { key: "valorLocomocionPaciente", label: "Movilizacion" },
    { key: "valorOtroPaciente", label: "Otro" },
];

const addMonths = (dateString, months) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.slice(0, 10).split("-").map(Number);
    if (!year || !month || !day) return "";
    const date = new Date(year, month - 1, day);
    date.setMonth(date.getMonth() + months);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const MONTHS = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
];

const normalizeText = (value) =>
    String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

const normalizeRut = (value) => normalizeText(value).replace(/[^0-9k]/g, "");

const getProximoReajuste = (row) => row.proximo_reajuste_familia || addMonths(row.ultimo_reajuste_familia, 6);
const siguienteKey = (campo) => `proximo_${campo}`;

const getEstadoCalendario = (row) => {
    const proximo = getProximoReajuste(row);
    if (!proximo) return { key: "programado", label: "-", className: "bg-light text-dark border" };
    const [year, month] = proximo.slice(0, 7).split("-").map(Number);
    const now = new Date();
    const diff = (year - now.getFullYear()) * 12 + (month - (now.getMonth() + 1));
    if (diff <= 0) return { key: "aplicar", label: "Aplicar reajuste", className: "bg-warning text-dark" };
    if (row.reajuste_familia_aviso_estado === "notificado") {
        return { key: "notificado", label: "Notificado", className: "bg-success" };
    }
    if (diff === 2) return { key: "notificar", label: "Notificar", className: "bg-info text-dark" };
    return { key: "programado", label: "-", className: "bg-light text-dark border" };
};

const dateSearchText = (dateString) => {
    if (!dateString) return "";
    const value = dateString.slice(0, 10);
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return normalizeText(value);
    const monthName = MONTHS[month - 1] || "";
    return normalizeText(`${value} ${day}-${month}-${year} ${day}-${String(month).padStart(2, "0")}-${year} ${monthName} ${monthName} ${year} ${String(month).padStart(2, "0")}-${year}`);
};

const moneySearchText = (value) => {
    const number = Number(value || 0);
    return `${number} ${formatMoney(number)}`.replace(/\./g, "");
};

const ReajusteValoresFamilia = () => {
    const [pacientes, setPacientes] = useState([]);
    const [modificaciones, setModificaciones] = useState({});
    const [busqueda, setBusqueda] = useState("");
    const [filtros, setFiltros] = useState({});
    const [filtroEstado, setFiltroEstado] = useState("todos");
    const [orden, setOrden] = useState({ key: "paciente", direction: "asc" });
    const [fechaReajuste, setFechaReajuste] = useState(() => new Date().toISOString().slice(0, 10));
    const [observacion, setObservacion] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [historial, setHistorial] = useState({ visible: false, paciente: null, data: [], loading: false });

    const cargarPacientes = async () => {
        setLoading(true);
        try {
            const resp = await axiosInstance.get("/paciente/reajuste-familia");
            if (resp.data?.error) throw new Error(resp.data.message || "No se pudo cargar la informacion.");
            setPacientes(resp.data.data || []);
        } catch (error) {
            gdAlertify.notiError(error?.message || "Error al cargar valores familia.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarPacientes();
    }, []);

    const columnValue = useCallback((row, key) => {
        if (key === "paciente") return `${row.nombre_paciente || ""} ${row.rut || ""} ${normalizeRut(row.rut)}`;
        if (key === "sector") return formatSectorLabel(row.sector);
        if (key === "ultimo_reajuste_familia") return row.ultimo_reajuste_familia || "";
        if (key === "proximo_reajuste_familia") return getProximoReajuste(row) || "";
        return row[key] ?? "";
    }, []);

    const columnSearchText = useCallback((row, key) => {
        if (key === "paciente") return normalizeText(columnValue(row, key));
        if (key === "sector") return normalizeText(columnValue(row, key));
        if (key === "ultimo_reajuste_familia" || key === "proximo_reajuste_familia") {
            return dateSearchText(columnValue(row, key));
        }
        if (CAMPOS.some((campo) => campo.key === key)) return moneySearchText(columnValue(row, key));
        return normalizeText(columnValue(row, key));
    }, [columnValue]);

    const pacientesFiltrados = useMemo(() => {
        const q = normalizeText(busqueda);
        const rutQ = normalizeRut(busqueda);
        const activeFilters = Object.entries(filtros)
            .map(([key, value]) => [key, normalizeText(value)])
            .filter(([, value]) => value);

        const filtered = pacientes.filter((p) => {
            const estado = getEstadoCalendario(p);
            if (filtroEstado !== "todos" && estado.key !== filtroEstado) return false;
            if (q) {
                const generalText = normalizeText([
                    p.nombre_paciente,
                    p.rut,
                    normalizeRut(p.rut),
                    formatSectorLabel(p.sector),
                    p.ultimo_reajuste_familia,
                    getProximoReajuste(p),
                    dateSearchText(p.ultimo_reajuste_familia),
                    dateSearchText(getProximoReajuste(p)),
                ].join(" "));

                if (!generalText.includes(q) && !(rutQ && normalizeRut(p.rut).includes(rutQ))) {
                    return false;
                }
            }

            return activeFilters.every(([key, value]) => {
                if (key === "paciente") {
                    const rutFilter = normalizeRut(value);
                    return columnSearchText(p, key).includes(value) || (rutFilter && normalizeRut(p.rut).includes(rutFilter));
                }
                return columnSearchText(p, key).includes(value);
            });
        });

        const sorted = [...filtered].sort((a, b) => {
            const av = columnValue(a, orden.key);
            const bv = columnValue(b, orden.key);
            const direction = orden.direction === "desc" ? -1 : 1;

            if (CAMPOS.some((campo) => campo.key === orden.key)) {
                return (Number(av || 0) - Number(bv || 0)) * direction;
            }

            if (orden.key === "ultimo_reajuste_familia" || orden.key === "proximo_reajuste_familia") {
                return String(av || "").localeCompare(String(bv || "")) * direction;
            }

            return normalizeText(av).localeCompare(normalizeText(bv)) * direction;
        });

        return sorted;
    }, [pacientes, busqueda, filtros, filtroEstado, orden, columnValue, columnSearchText]);

    const totalCambios = Object.values(modificaciones).reduce((acc, cambios) => acc + Object.keys(cambios).length, 0);
    const totalPacientes = Object.keys(modificaciones).length;

    const getValorSiguiente = (row, campo) => {
        const cambio = modificaciones[row.pacienteID]?.[campo];
        if (cambio !== undefined) return cambio ?? "";
        return row[siguienteKey(campo)] ?? "";
    };

    const getFecha = (row, campo) => {
        const cambio = modificaciones[row.pacienteID]?.[campo];
        const actual = row[campo] || (campo === "proximo_reajuste_familia" ? getProximoReajuste(row) : "");
        return cambio ?? actual ?? "";
    };

    const setFiltro = (key, value) => {
        setFiltros((prev) => ({ ...prev, [key]: value }));
    };

    const cambiarOrden = (key) => {
        setOrden((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const sortIcon = (key) => {
        if (orden.key !== key) return "bi-arrow-down-up text-muted";
        return orden.direction === "asc" ? "bi-sort-alpha-down" : "bi-sort-alpha-up";
    };

    const headerButton = (key, label, className = "") => (
        <button
            type="button"
            className={`btn btn-link btn-sm p-0 text-decoration-none fw-bold text-dark ${className}`}
            onClick={() => cambiarOrden(key)}
        >
            {label} <i className={`bi ${sortIcon(key)} ms-1`}></i>
        </button>
    );

    const filtroInput = (key, placeholder = "Filtrar") => (
        <input
            className="form-control form-control-sm"
            value={filtros[key] || ""}
            onChange={(e) => setFiltro(key, e.target.value)}
            placeholder={placeholder}
        />
    );

    const setValor = (row, campo, value) => {
        setModificaciones((prev) => {
            const next = { ...prev };
            const pacienteCambios = { ...(next[row.pacienteID] || {}) };
            const nuevo = value === "" ? null : Number(value);
            const programado = row[siguienteKey(campo)] === null || row[siguienteKey(campo)] === undefined
                ? null
                : Number(row[siguienteKey(campo)]);

            if (nuevo === programado) {
                delete pacienteCambios[campo];
            } else {
                pacienteCambios[campo] = nuevo;
            }

            if (Object.keys(pacienteCambios).length === 0) {
                delete next[row.pacienteID];
            } else {
                next[row.pacienteID] = pacienteCambios;
            }

            return next;
        });
    };

    const setFecha = (row, campo, value) => {
        setModificaciones((prev) => {
            const next = { ...prev };
            const pacienteCambios = { ...(next[row.pacienteID] || {}) };
            const actual = row[campo] || (campo === "proximo_reajuste_familia" ? addMonths(row.ultimo_reajuste_familia, 6) : "");
            const nuevo = value || null;

            if (!nuevo || nuevo === actual) {
                delete pacienteCambios[campo];
            } else {
                pacienteCambios[campo] = nuevo;
            }

            if (Object.keys(pacienteCambios).length === 0) {
                delete next[row.pacienteID];
            } else {
                next[row.pacienteID] = pacienteCambios;
            }

            return next;
        });
    };

    const guardar = () => {
        if (totalPacientes === 0) {
            gdAlertify.notiError("No hay cambios para guardar.");
            return;
        }

        const datos = pacientes
            .filter((p) => modificaciones[p.pacienteID])
            .map((p) => ({
                pacienteID: p.pacienteID,
                ...modificaciones[p.pacienteID],
                proximo_reajuste_familia: modificaciones[p.pacienteID]?.proximo_reajuste_familia
                    || p.proximo_reajuste_familia
                    || fechaReajuste,
            }));

        gdAlertify.confirm({
            title: "Programar valores siguientes",
            message: `Se programaran ${totalCambios} cambio(s) en ${totalPacientes} paciente(s), sin modificar los valores actuales.`,
            onConfirm: async () => {
                setSaving(true);
                try {
                    const resp = await axiosInstance.post("/paciente/reajuste-familia/programar", {
                        datos,
                        fechaReajuste,
                        observacion,
                    });
                    if (resp.data?.error) throw new Error(resp.data.message || "No se pudo guardar.");
                    gdAlertify.notiSuccess(resp.data.message || "Reajuste guardado.");
                    setModificaciones({});
                    setObservacion("");
                    cargarPacientes();
                } catch (error) {
                    gdAlertify.notiError(error.response?.data?.message || error.message || "Error al guardar reajuste.");
                } finally {
                    setSaving(false);
                }
            },
            onCancel: () => {}
        });
    };

    const aplicarReajuste = (row) => {
        const fecha = getProximoReajuste(row);
        gdAlertify.confirm({
            title: "Aplicar reajuste",
            message: `¿Aplicar los valores siguientes de ${row.nombre_paciente} desde ${formatDate(fecha)}?`,
            onConfirm: async () => {
                try {
                    await axiosInstance.post("/paciente/reajuste-familia/resolver", {
                        pacienteID: row.pacienteID,
                        accion: "aplicar",
                        fecha,
                        observacion,
                    });
                    gdAlertify.notiSuccess("Reajuste aplicado correctamente.");
                    setModificaciones({});
                    cargarPacientes();
                } catch (error) {
                    gdAlertify.notiError(error.response?.data?.message || "No se pudo resolver el ciclo.");
                }
            },
            onCancel: () => {}
        });
    };

    const abrirHistorial = async (paciente) => {
        setHistorial({ visible: true, paciente, data: [], loading: true });
        try {
            const resp = await axiosInstance.get(`/paciente/reajuste-familia/historial/${paciente.pacienteID}`);
            setHistorial({ visible: true, paciente, data: resp.data?.data || [], loading: false });
        } catch (error) {
            gdAlertify.notiError("No se pudo cargar el historial.");
            setHistorial({ visible: true, paciente, data: [], loading: false });
        }
    };

    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h3 className="mb-1">Reajuste Valores Familia</h3>
                            <div className="text-muted small">Programa valores siguientes y gestiona la revisión semestral sin alterar anticipadamente los valores actuales.</div>
                        </div>
                        {totalPacientes > 0 && (
                            <span className="badge bg-warning text-dark">
                                {totalPacientes} paciente(s), {totalCambios} cambio(s)
                            </span>
                        )}
                    </div>

                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label">Buscar</label>
                            <input
                                className="form-control"
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                placeholder="Nombre, RUT o sector"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Estado</label>
                            <select className="form-select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                                <option value="todos">Todos</option>
                                <option value="notificar">Notificar</option>
                                <option value="notificado">Notificado</option>
                                <option value="aplicar">Aplicar reajuste</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Fecha a programar/postergar</label>
                            <input
                                type="date"
                                className="form-control"
                                value={fechaReajuste}
                                onChange={(e) => setFechaReajuste(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Observación</label>
                            <input
                                className="form-control"
                                value={observacion}
                                onChange={(e) => setObservacion(e.target.value)}
                                placeholder="Motivo o comentario opcional"
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn btn-primary w-100 mb-0"
                                onClick={guardar}
                                disabled={saving || totalPacientes === 0}
                            >
                                {saving ? "Guardando..." : "Programar"}
                            </button>
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted small">
                            Mostrando {pacientesFiltrados.length} de {pacientes.length} paciente(s)
                        </div>
                        {(busqueda || filtroEstado !== "todos" || Object.values(filtros).some(Boolean)) && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary mb-0"
                                onClick={() => {
                                    setBusqueda("");
                                    setFiltros({});
                                    setFiltroEstado("todos");
                                }}
                            >
                                Limpiar filtros
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="card mt-3">
                <div className="card-body">
                    {loading ? (
                        <div className="text-muted">Cargando pacientes...</div>
                    ) : (
                        <div className="table-responsive" style={{ maxHeight: 680 }}>
                            <table className="table table-sm table-bordered align-middle">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th>{headerButton("paciente", "Paciente")}</th>
                                        <th className="text-center">{headerButton("sector", "Sector")}</th>
                                        <th className="text-center">{headerButton("ultimo_reajuste_familia", "Ultimo reajuste")}</th>
                                        <th className="text-center">{headerButton("proximo_reajuste_familia", "Proximo reajuste")}</th>
                                        <th className="text-center">Estado ciclo</th>
                                        {CAMPOS.map((campo) => (
                                            <th className="text-end" key={campo.key}>{campo.label}: actual / siguiente</th>
                                        ))}
                                        <th className="text-center">Gestión</th>
                                    </tr>
                                    <tr>
                                        <th style={{ minWidth: 210 }}>{filtroInput("paciente", "Nombre o RUT")}</th>
                                        <th style={{ minWidth: 120 }}>{filtroInput("sector", "Sector")}</th>
                                        <th style={{ minWidth: 145 }}>{filtroInput("ultimo_reajuste_familia", "Ej: junio")}</th>
                                        <th style={{ minWidth: 150 }}>{filtroInput("proximo_reajuste_familia", "Ej: junio")}</th>
                                        <th></th>
                                        {CAMPOS.map((campo) => (
                                            <th key={`filter-${campo.key}`} style={{ minWidth: 130 }}>
                                                {filtroInput(campo.key, "$")}
                                            </th>
                                        ))}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pacientesFiltrados.map((row) => {
                                        const hasChange = Boolean(modificaciones[row.pacienteID]);
                                        const estadoCalendario = getEstadoCalendario(row);
                                        const tieneValoresSiguientes = CAMPOS.some((campo) => row[siguienteKey(campo.key)] !== null && row[siguienteKey(campo.key)] !== undefined);
                                        return (
                                            <tr key={row.pacienteID} className={hasChange ? "table-warning" : ""}>
                                                <td>
                                                    <div className="fw-bold">{row.nombre_paciente}</div>
                                                    <div className="text-muted small">{row.rut}</div>
                                                </td>
                                                <td className="text-center">{formatSectorLabel(row.sector)}</td>
                                                <td className="text-center">
                                                    {row.ultimo_reajuste_familia ? formatDate(row.ultimo_reajuste_familia) : "-"}
                                                </td>
                                                <td style={{ minWidth: 150 }}>
                                                    <input
                                                        type="date"
                                                        className={`form-control form-control-sm ${modificaciones[row.pacienteID]?.proximo_reajuste_familia !== undefined ? "border-warning fw-bold" : ""}`}
                                                        value={getFecha(row, "proximo_reajuste_familia")}
                                                        onChange={(e) => setFecha(row, "proximo_reajuste_familia", e.target.value)}
                                                    />
                                                    <div className="text-muted small text-end">
                                                        Actual: {row.proximo_reajuste_familia ? formatDate(row.proximo_reajuste_familia) : "-"}
                                                    </div>
                                                </td>
                                                <td className="text-center" style={{ minWidth: 170 }}>
                                                    <span className={`badge ${estadoCalendario.className}`}>{estadoCalendario.label}</span>
                                                    {tieneValoresSiguientes && <div className="small text-primary">Valores preparados</div>}
                                                </td>
                                                {CAMPOS.map((campo) => (
                                                    <td key={campo.key} style={{ minWidth: 130 }}>
                                                        <div className="small text-end fw-bold mb-1">
                                                            Actual: ${formatMoney(row[campo.key] || 0)}
                                                        </div>
                                                        <div className="input-group input-group-sm">
                                                            <span className="input-group-text">$</span>
                                                            <input
                                                                type="number"
                                                                className={`form-control text-end ${modificaciones[row.pacienteID]?.[campo.key] !== undefined ? "border-warning fw-bold" : ""}`}
                                                                value={getValorSiguiente(row, campo.key)}
                                                                onChange={(e) => setValor(row, campo.key, e.target.value)}
                                                                min="0"
                                                                step="1000"
                                                                placeholder="Sin programar"
                                                            />
                                                        </div>
                                                        <div className="text-muted small text-end">
                                                            Siguiente {row[siguienteKey(campo.key)] === null || row[siguienteKey(campo.key)] === undefined
                                                                ? "sin definir"
                                                                : `$${formatMoney(row[siguienteKey(campo.key)])}`}
                                                        </div>
                                                    </td>
                                                ))}
                                                <td className="text-center" style={{ minWidth: 190 }}>
                                                    <div className="d-flex flex-wrap justify-content-center gap-1">
                                                        <button className="btn btn-sm btn-outline-secondary mb-0" onClick={() => abrirHistorial(row)} title="Historial">
                                                            <i className="bi bi-clock-history"></i>
                                                        </button>
                                                        {estadoCalendario.key === "aplicar" && (
                                                            <button className="btn btn-sm btn-success mb-0" disabled={!tieneValoresSiguientes} onClick={() => aplicarReajuste(row)}>
                                                                Aplicar reajuste
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {historial.visible && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.35)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Historial reajustes - {historial.paciente?.nombre_paciente}</h5>
                                <button type="button" className="btn-close" onClick={() => setHistorial({ visible: false, paciente: null, data: [], loading: false })}></button>
                            </div>
                            <div className="modal-body">
                                {historial.loading ? (
                                    <div className="text-muted">Cargando historial...</div>
                                ) : historial.data.length === 0 ? (
                                    <div className="text-muted">Sin historial registrado.</div>
                                ) : (
                                    <table className="table table-sm table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Campo</th>
                                                <th className="text-end">Anterior</th>
                                                <th className="text-end">Nuevo</th>
                                                <th>Usuario</th>
                                                <th>Observacion</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historial.data.map((row) => (
                                                <tr key={row.id}>
                                                    <td>{formatDate(row.fecha_reajuste)}</td>
                                                    <td>{CAMPOS.find(c => c.key === row.campo)?.label
                                                        || (row.campo?.startsWith("programado:")
                                                            ? `Programado: ${CAMPOS.find(c => c.key === row.campo.split(":")[1])?.label || row.campo.split(":")[1]}`
                                                            : row.campo?.replace("ciclo:", "Ciclo: ").replace("aviso:", "Aviso: "))}</td>
                                                    <td className="text-end">${formatMoney(row.valor_anterior)}</td>
                                                    <td className="text-end">${formatMoney(row.valor_nuevo)}</td>
                                                    <td>{row.usuario_nombre || "-"}</td>
                                                    <td>{row.observacion || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReajusteValoresFamilia;
